import { useQuery } from '@tanstack/react-query';
import { chunk } from 'lodash';
import { IAssemblyComponent } from 'src/interfaces';
import { generateQuery, requestCCApi } from 'src/utils';
import { queryKeys } from 'src/utils/queryKeys';

export const useGetAllAssemblyComponents = (
  options = {},
  filters: string[] = [],
) =>
  useQuery({
    queryKey: queryKeys.assemblyComponents.all(filters),
    queryFn: () => fetchAllAssemblyComponents(filters),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...options,
  });

export const fetchAllAssemblyComponents = async (filters: string[] = []) => {
  const url = `/assemblyComponents${generateQuery(
    [],
    [...filters, 'pageSize=0'],
  )}`;

  const response = await requestCCApi({ url });

  return response.data as IAssemblyComponent[];
};

// Keeps each request's `assembly_id.list=...` segment well under common URL
// limits (~8KB on nginx/CDNs). 200 IDs × ~7 chars (id + pipe) ≈ 1.4KB.
export const ASSEMBLY_ID_CHUNK_SIZE = 200;

export const fetchAllAssemblyComponentsByAssemblyIds = async (
  assemblyIds: number[],
  extraFilters: string[] = [],
): Promise<IAssemblyComponent[]> => {
  if (assemblyIds.length === 0) {
    return [];
  }

  const chunks = chunk(assemblyIds, ASSEMBLY_ID_CHUNK_SIZE);
  const responses = await Promise.all(
    chunks.map((ids) =>
      fetchAllAssemblyComponents([
        `assembly_id.list=${ids.join('|')}`,
        ...extraFilters,
      ]),
    ),
  );

  return responses.flat();
};

export const useGetAllAssemblyComponentsByAssemblyIds = (
  assemblyIds: number[],
  extraFilters: string[] = [],
  options = {},
) => {
  const sortedIds = [...assemblyIds].sort((a, b) => a - b);

  return useQuery({
    queryKey: queryKeys.assemblyComponents.all([
      `assembly_id.list=${sortedIds.join('|')}`,
      ...extraFilters,
    ]),
    queryFn: () =>
      fetchAllAssemblyComponentsByAssemblyIds(sortedIds, extraFilters),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...options,
  });
};
